import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TextField,
  Grow,
  Icon,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import { getQuoteMarketingSegmentsCustomers, getEavAttributeApi } from 'api';
import { EditIcon } from 'components/CustomSVG';
import { useStyles } from './styles';
import SegmentsCustomerEdit from './SegmentsCustomerEdit';


const SegmentsCustomersTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [customers, setCustomers] = useState([]);
  const [openSegmentsCustomerEdit, setOpenSegmentsCustomerEdit] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  const {
    marketingSegmentsCustomers,
    marketingSegmentsCustomersTotalCount
  } = useSelector((state: IReduxState) => state.quote_marketing_segments);
  const { options } = useSelector((state: IReduxState) => state.eavAttributes);

  useEffect(() => {
    getQuoteMarketingSegmentsCustomers({ dispatch });

    getEavAttributeApi({
      dispatch,
      entityType: 1,
      attributeCode: 'marketing_segments'
    });
  }, []);

  useEffect(() => {
    if (marketingSegmentsCustomers?.length) {
      setCustomers(marketingSegmentsCustomers.map((customer) => {
        const phoneNumberAttr = customer.custom_attributes?.find((el) => el.attribute_code === 'phone_number')?.value || '';
        const customerMarketingSegmentsValues = customer.custom_attributes?.find((el) => el.attribute_code === 'marketing_segments')?.value?.split(',');

        const activeMarketingSegments = options.reduce((acc, segment) => {
          if (customerMarketingSegmentsValues?.includes(segment?.value)) {
            acc.push(segment);
          }

          return acc;
        }, []);

        return ({
          ...customer,
          login_phone: phoneNumberAttr,
          marketing_segments: activeMarketingSegments
        });
      }));
    } else {
      setCustomers([]);
    }
  }, [marketingSegmentsCustomersTotalCount, marketingSegmentsCustomers]);

  const columns = [
    {
      label: 'First Name',
      name: 'firstname',
      options: {
        sort: true,
        filter: true,
        setCellHeaderProps: () => ({ style: { width: '150px' } }),
        customBodyRenderLite: (dataIndex) => {
          const customer = customers[dataIndex];
          return (
            <Typography className="pr-2 whitespace-no-wrap" noWrap>
              {customer?.firstname}
            </Typography>
          );
        },
      },
    },
    {
      label: 'Last Name',
      name: 'lastname',
      options: {
        sort: true,
        filter: true,
        setCellHeaderProps: () => ({ style: { width: '150px' } }),
        customBodyRenderLite: (dataIndex) => {
          const customer = customers[dataIndex];
          return (
            <Typography className="pr-2 whitespace-no-wrap" noWrap>
              {customer?.lastname}
            </Typography>
          );
        },
      },
    },
    {
      label: 'Email',
      name: 'email',
      options: {
        sort: true,
        filter: true,
        setCellHeaderProps: () => ({ style: { width: '250px' } }),
        customBodyRenderLite: (dataIndex) => {
          const customer = customers[dataIndex];
          return (
            <Typography className="pr-2 whitespace-no-wrap" noWrap>
              {customer?.email}
            </Typography>
          );
        },
      },
    },
    {
      label: 'Phone',
      name: 'login_phone',
      options: {
        sort: true,
        filter: true,
        filterType: 'custom',
        customFilterListOptions: {
          render: (val) => val[0] !== '1' ? 'Without Phone' : 'With Phone',
          update: (filterList, _filterPos, index) => {
            filterList[index] = [];
            return filterList;
          }
        },
        filterOptions: {
          logic: (phone, filters) => {
            if (filters.length) {
              if (filters[0] === '1') {
                return !phone?.length;
              } else if (filters[0] === '0') {
                return !!phone?.length;
              }
            }

            return false;
          },
          renderValue: (val) => val !== '1' ? 'Without Phone' : 'With Phone',
          display: (filterList, onChange, index, column) => {
            return (
              <FormControl variant="standard">
                <Select
                  value={filterList[index]}
                  className="mt-4"
                  id="select"
                  displayEmpty
                  onChange={(event) => {
                    const { value } = event.target;
                    filterList[index][0] = value;
                    onChange(filterList[index], index, column);
                  }}
                >
                  <MenuItem disabled value="">
                    <span className="text-muted">Phone Number</span>
                  </MenuItem>
                  <MenuItem value="1">With Phone</MenuItem>
                  <MenuItem value="0">Without Phone</MenuItem>
                </Select>
              </FormControl>
            );
          }
        },
        setCellHeaderProps: () => ({ style: { width: '150px' } }),
        customBodyRenderLite: (dataIndex) => {
          const customer = customers[dataIndex];
          return (
            <Typography className="pr-2 whitespace-no-wrap" noWrap>
              {customer?.login_phone}
            </Typography>
          );
        },
      },
    },
    {
      label: 'Marketing Segments',
      name: 'marketing_segments',
      options: {
        filter: true,
        filterType: 'custom',
        customFilterListOptions: {
          render: (val) => {
            const filterValues = val.filter((el) => el != undefined);
            return filterValues[0] !== '0' ? filterValues : 'Without Marketing Segments';
          },
          update: (filterList, _filterPos, index) => {
            filterList[index] = [];
            return filterList;
          }
        },
        sortCompare: (order) => {
          return (a, b) => {
            const labelsA = a.data?.map((segment) => segment.label).join('') || '';
            const labelsB = b.data?.map((segment) => segment.label).join('') || '';

            return order === 'asc' ? labelsA.localeCompare(labelsB) : labelsB.localeCompare(labelsA);
          };
        },
        filterOptions: {
          fullWidth: true,
          logic: (segment, filters) => {
            if (filters.length) {
              if (filters[0] === '0') {
                return !!segment.length;
              }

              const segmentLabels = segment?.map((el) => el?.label);

              if (filters[1]?.length) {
                return segmentLabels?.includes(filters[1]);
              }

              return !segmentLabels?.includes(filters[0]);
            }

            return false;
          },
          renderValue: (val) => val !== '0' ? val : 'Without Marketing Segments',
          display: (filterList, onChange, index, column) => {
            return (
              <div className="flex justify-between">
                <FormControl variant="standard" className="w-full mr-4">
                  <InputLabel htmlFor="marketing-segmen" shrink={!!filterList[index][0]}>Marketing Segments</InputLabel>
                  <Select
                    value={filterList[index]}
                    className="mt-4"
                    id="marketing-segmen"
                    disabled={!!filterList[index][1]?.length}
                    displayEmpty
                    onChange={(event) => {
                      const { value } = event.target;
                      filterList[index][0] = value;
                      onChange(filterList[index], index, column);
                    }}
                  >
                    {options.map((el) => <MenuItem key={el.value} value={el.label}>{el.label}</MenuItem>)}
                    <MenuItem value="0">Without Marketing Segments</MenuItem>
                  </Select>
                </FormControl>
                <FormControl className="w-full ml-4">
                  <InputLabel htmlFor="exclude-marketing-segmen" shrink={!!filterList[index][1]}>Exclude Marketing Segments</InputLabel>
                  <Select
                    value={filterList[index][1] || ''}
                    className="mt-4"
                    id="exclude-marketing-segmen"
                    displayEmpty
                    disabled={!!filterList[index][0]?.length}
                    onChange={(event) => {
                      const { value } = event.target;

                      if (value) {
                        filterList[index][1] = value;
                        onChange(filterList[index], index, column);
                      }
                    }}
                  >
                    {options.map((el) => <MenuItem key={el.value} value={el.label}>{el.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </div>
            );
          }
        },
        setCellHeaderProps: () => ({ style: { width: '150px' } }),
        customBodyRenderLite: (dataIndex) => {
          const customer = customers[dataIndex];
          return (
            customer.marketing_segments?.map((el) => (
              <Typography key={el?.value} className="whitespace-no-wrap">
                <Chip
                  color="primary"
                  label={el?.label}
                />
              </Typography>
            ))
          );
        },
      },
    },
    {
      label: 'Details',
      name: 'details',
      options: {
        sort: false,
        filter: false,
        setCellHeaderProps: () => ({ style: { width: '80px' } }),
        customBodyRenderLite: (dataIndex) => {
          const customer = customers[dataIndex];

          return (
            <Tooltip title="Edit">
              <IconButton
                onClick={() => handleOpenEditForm(customer?.id)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          );
        },
      },
    },
  ];

  const handleOpenEditForm = (id) => {
    setSelectedCustomerId(id);
    setOpenSegmentsCustomerEdit(true);
  };

  const handleRowsPerPageChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page + 1);
  };

  return (
    <div>
      <div className="table-container">
        <MUIDataTable
          className={classes.root}
          data={customers}
          columns={columns}
          options={{
            filterArrayFullMatch: false,
            filterType: 'textField',
            responsive: 'standard',
            selectableRows: 'none',
            search: true,
            filter: true,
            download: false,
            textLabels: {
              toolbar: {
                downloadCsv: 'Click here to download your current SKUs in an Excel spreadsheet'
              }
            },
            print: false,
            pagination: true,
            viewColumns: true,
            elevation: 0,
            searchPlaceholder: 'Enter product name',
            rowsPerPage: Number(pageSize),
            rowsPerPageOptions: [20, 40, 80, 100],
            page: Number(currentPage - 1),
            customToolbarSelect: () => (<div />),
            onChangePage: (page) => {
              handlePageChange(page);
            },
            onChangeRowsPerPage: (rows) => {
              handleRowsPerPageChange(rows);
            },
            isRowSelectable: (dataIndex) => {
              const queueTasksCustomers = []; ///TBD: get from api queue of customers
              const isQueued = queueTasksCustomers.includes(marketingSegmentsCustomers[dataIndex].email);
              return !isQueued;
            },
            customSearchRender: (
              searchText,
              handleSearch,
              hideSearch
            ) => {
              return (
                <Grow appear in timeout={300}>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    onChange={({ target: { value } }) =>
                      handleSearch(value)
                    }
                    InputProps={{
                      style: {
                        paddingRight: 0,
                      },
                      startAdornment: (
                        <Icon
                          className="mr-2"
                          fontSize="small"
                        >
                          search
                        </Icon>
                      ),
                      endAdornment: (
                        <IconButton
                          onClick={hideSearch}
                        >
                          <Icon fontSize="small">
                            clear
                          </Icon>
                        </IconButton>
                      ),
                    }}
                  />
                </Grow>
              );
            }
          }}
        />
      </div>
      <Dialog
        open={openSegmentsCustomerEdit}
        onClose={() => setOpenSegmentsCustomerEdit(false)}
        maxWidth={'md'}
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h4" className="mt-4">Edit Customer</Typography>
        </DialogTitle>
        <DialogContent className="mt-4">
          <SegmentsCustomerEdit customerId={selectedCustomerId} setOpenSegmentsCustomerEdit={setOpenSegmentsCustomerEdit} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SegmentsCustomersTable;
